
import CropFilter from "@/components/Farm/CropFilter.vue";
import SearchResult from "@/components/Sidebar/SearchResult.vue";
import SetCropsButton from "@/components/Farm/SetCropsButton.vue";
import { formatDate, gettersInstance, groupBy, logError } from "@/lib/util";
import debounce from "debounce";
import { Component, MixedVue, mixins } from "@/vue";
import { RawLocation } from "vue-router";
import { Watch } from "vue-property-decorator";
import { Parcel } from "@/lib/types/Parcel";
import { GroupedResultsArray } from "@/lib/types/misc/GroupedResultsArray";
import VirtualScroll from "../VirtualScroll.vue";
import SetCropsModal from "../Modals/SetCropsModal.vue";
import { downloadApiCall } from "@/lib/api";
import EventBus from "@/lib/event-bus";

const StoreGetters = gettersInstance([
  "rasterKind",
  "selectedCropId",
  "rastersSelectable",
  "currentFarm",
  "currentFarmId",
  "currentSeasonId",
  "selectedDate",
  "parcelsForCurrentSeason",
  "userHasAccessToFarm",
  "userHasFullAccessToFarm",
  "parcelSearchText",
  "filteredParcels",
  "filteredParcelSize",
  "sidebarParcels",
  "selectedParcels",
  "parcelSearchText",
  "selectedParcelId",
]);

@Component({
  name: "SearchSidebar",
  components: { SetCropsButton, SearchResult, CropFilter, VirtualScroll, SetCropsModal },
})
export default class SearchSidebar extends mixins(MixedVue, StoreGetters) {
  static components: {
    SetCropsButton: typeof SetCropsButton;
    SearchResult: typeof SearchResult;
    CropFilter: typeof CropFilter;
    VirtualScroll: typeof VirtualScroll;
  };

  maxPage = 100000;
  internalSearchText = "";
  showModal = false;

  searchTextDebounced = debounce((newValue: string) => {
    this.$store.commit("setParcelSearchText", newValue);
  }, 200);

  /**
   * Placeholder for the search input
   */
  get searchPlaceholder() {
    if (this.sidebarParcels.length <= 0) {
      return this.$gettext("No parcels");
    }
    return this.$gettextInterpolate(this.$ngettext("%{ n } parcel. Search by name", "%{ n } parcels. Search by name", this.sidebarParcels.length), {
      n: this.sidebarParcels.length,
    });
  }

  get searchResultsParcels() {
    return this.filteredParcels.filter((parcel) => !parcel.selected);
  }

  get searchResultsGrouped(): GroupedResultsArray<{ cropId: string }, { parcel: Parcel }> {
    const grouped = groupBy(this.searchResultsParcels, (parcel) => this.getCropIntervalCrop(parcel.crop_intervals));
    return Object.keys(grouped).flatMap((cropId) => {
      const parcels = grouped[cropId];
      return [
        { type: "header" as "header", cropId, key: "h" + cropId },
        ...parcels.map((parcel) => ({ type: "item" as "item", parcel, key: "p" + parcel.id })),
      ];
    });
  }

  get selectedParcelIds() {
    return this.selectedParcels.map((parcel) => parcel.id);
  }

  get selectedParcelSize() {
    return this.selectedParcels.reduce((result, parcel) => result + parcel.hectares, 0);
  }

  get routeToMap(): RawLocation {
    return {
      name: "map-season-selected",
      params: {
        farmId: this.currentFarmId,
        seasonId: this.currentSeasonId,
      },
    };
  }

  get routeToCompare(): RawLocation {
    return {
      name: "compare-parcels",
      params: {
        farmId: this.currentFarmId,
        seasonId: this.currentSeasonId,
      },
      query: {
        backToMap: "true", // TODO????
        date: formatDate(this.selectedDate),
        parcel: this.selectedParcelIds,
        rasterKind: this.rasterKind,
      },
    };
  }

  get showCompareTools() {
    return this.rastersSelectable;
  }

  @Watch("internalSearchText")
  internalSearchTextChanged() {
    this.searchTextDebounced(this.internalSearchText);
  }

  @Watch("currentFarmId")
  currentFarmIdChanged() {
    this.$store.commit("setParcelSearchText", "");
    this.$store.commit("setCropSelected", null);
    this.internalSearchText = "";
  }

  beforeDestroy() {
    this.searchTextDebounced?.clear();
  }

  deselectCrop() {
    this.$store.commit("setCropSelected", null);
  }

  setShowModal(value: boolean, selectedParcelIds: string[]) {
    EventBus.$emit("set-crops-modal", value, selectedParcelIds);
  }

  async downloadSelectedParcels() {
    if (!this.selectedParcelIds.length) {
      return;
    }

    try {
      await downloadApiCall(
        "POST",
        `/farm/${this.currentFarmId}/season/${this.currentSeasonId}/download_season_shapes/`,
        {},
        {
          parcels: this.selectedParcelIds,
        },
        "application/json",
        "*/*"
      );
    } catch (e) {
      logError(e);
      this.notify({
        type: "error",
        text: this.$gettext("Unable to download selected parcels"),
      });
    }
  }

  clickSelectAll() {
    if (this.selectedParcels.length > 0) {
      this.$store.commit("clearAllSelectedParcels");
    } else {
      this.$store.commit("setRastersSelectable", true);
      this.filteredParcels.forEach((parcel) => {
        this.$store.commit("setParcelSelected", {
          parcelId: parcel.id,
          selected: true,
        });
      });
    }
  }

  async onCropsChanged() {
    await this.$store.dispatch("refreshSeasonCrops", this.currentSeasonId);
    await this.$store.dispatch("getParcelsForCurrentSeason");
  }

  scrollToSearchResults() {
    this.$nextTick(() => {
      const scrollableEl = this.$el;
      const searchListEl: HTMLElement = this.$el.querySelector(".search-result-list");
      if (!scrollableEl || !searchListEl) return;

      scrollableEl.scrollTo(0, searchListEl.offsetTop - 110);
    });
  }
}
